<script lang="ts">
	import {
		DropdownMenu,
		MenuItemList,
		type IconButtonPropsTypes,
	} from '@Shelly/shelly-design-system/index';
	import OptionManagmentModal from 'src/components/OptionManagmentModal.svelte';
	import TabsActionsButton from '../../TabsActionsButton.svelte';
	import ListHeaderSearch from 'src/components/Lists/ListHeaderSearch.svelte';
	import { is_mobile, is_tablet } from 'src/stores';
	import { mapIconSolidWeightToLight } from 'src/helpers/new-layout-helpers';
	import { listHeader } from 'src/stores/list_header';

	/**
	 * Kebab (⋮) driven by the shared `listHeader` store. Renders a desktop
	 * dropdown or a mobile/tablet full-screen options modal, mirroring the
	 * `DashActions` pattern so both feel identical to users.
	 */
	export let triggerVariant: IconButtonPropsTypes['variant'] = 'help-2';
	/**
	 * Optional overriding aria-label. When empty, `TabsActionsButton` falls
	 * back to the translated "More" string — preferred for consistency.
	 */
	export let ariaLabel = '';

	let showDropdown = false;
	let showModal = false;

	const toggle = () => {
		if ($is_tablet || $is_mobile) {
			showModal = !showModal;
		} else {
			showDropdown = !showDropdown;
		}
	};

	$: menuItems = $listHeader?.menuItems ?? [];
	$: search = $listHeader?.search ?? null;
	$: showKebabSearch = !!search && ($is_mobile || $is_tablet);
	$: hasMenu = menuItems.length > 0 || showKebabSearch;
	$: searchForModal =
		search && showKebabSearch
			? {
					...search,
					filter: search.filter
						? {
								...search.filter,
								onClick: () => {
									showModal = false;
									search.filter?.onClick();
								},
							}
						: undefined,
				}
			: null;

	// Collapse every group after the first into the modal's bottom section so
	// menus with more than two groups still render on tablet/mobile.
	$: modalConfig = {
		topSectionTitle: menuItems[0]?.title || '',
		itemsTop: (menuItems[0]?.items || []).map(mapItem),
		bottomSectionTitle: menuItems[1]?.title || '',
		itemsBottom: menuItems
			.slice(1)
			.flatMap((group) => group?.items || [])
			.map(mapItem),
	};

	function mapItem(item) {
		return {
			...item,
			title: item.title,
			titleDefault: item.title,
			icon: mapIconSolidWeightToLight(item.iconMobile || item.icon),
			disabled: item.disabled || false,
			onClick: item.onClick,
		};
	}
</script>

{#if hasMenu}
	<DropdownMenu bind:show={showDropdown}>
		<TabsActionsButton
			slot="trigger"
			{ariaLabel}
			variant={triggerVariant}
			isActive={showDropdown}
			on:click={toggle}
		/>

		{#each menuItems as groupItem, groupIndex}
			{#if groupItem.title}
				<MenuItemList
					isGroupTitle={groupItem.isGroupTitle ?? true}
					size="m"
					title={groupItem.title}
				/>
			{:else if groupIndex > 0}
				<!-- Title-less group renders as a standalone divider (matches DashActions). -->
				<div class="list-actions-divider"></div>
			{/if}

			{#each groupItem.items as menuListItem}
				<MenuItemList
					isGroupItem
					size="m"
					title={menuListItem.title}
					titleClasses={menuListItem.titleClasses}
					icon={menuListItem.icon}
					iconStyle={menuListItem?.iconStyle}
					disabled={menuListItem.disabled}
					on:click={() => {
						toggle();
						menuListItem.onClick?.(menuListItem);
					}}
				/>
			{/each}
		{/each}
	</DropdownMenu>

	<OptionManagmentModal
		bind:showModal
		topSectionTitle={modalConfig.topSectionTitle}
		bottomSectionTitle={modalConfig.bottomSectionTitle}
		itemsTop={modalConfig.itemsTop}
		itemsBottom={modalConfig.itemsBottom}
	>
		<div slot="header">
			{#if showKebabSearch && searchForModal}
				<ListHeaderSearch search={searchForModal} className="mb-m" />
			{/if}
		</div>
	</OptionManagmentModal>
{/if}

<style>
	.list-actions-divider {
		border-top: 1px solid var(--color-3);
	}
</style>
